import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  link: string | null;
  is_read: boolean;
  metadata: any;
  source_agent: string | null;
  created_at: string;
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      // Fetch notifications where user is the recipient (by email) or direct user_id match for admin/system notifications
      const userEmail = user.email?.toLowerCase();
      
      // RLS now handles matching by user_id OR recipient_email via auth.jwt()
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(50);

      if (!error && data) {
        // Client-side filter: show notifications where user is the actual recipient, not the sender
        const filtered = (data as any[]).filter((n) => {
          // If recipient_email is set, only show to the recipient
          if (n.recipient_email) {
            return n.recipient_email === userEmail;
          }
          // For system/admin notifications without recipient_email, show to the user_id owner
          return n.user_id === user.id;
        });
        setNotifications(filtered as Notification[]);
        setUnreadCount(filtered.filter((n: any) => !n.is_read).length);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Real-time subscription — listen for notifications targeted to this user's email
  useEffect(() => {
    if (!user?.email) return;
    const userEmail = user.email.toLowerCase();
    const channel = supabase
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
        },
        (payload) => {
          const newNotif = payload.new as any;
          // Only show if this notification is for the current user
          const isRecipient = newNotif.recipient_email === userEmail;
          const isDirectTarget = !newNotif.recipient_email && newNotif.user_id === user.id;
          if (isRecipient || isDirectTarget) {
            setNotifications((prev) => [newNotif as Notification, ...prev]);
            setUnreadCount((prev) => prev + 1);
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const markAsRead = useCallback(async (id: string) => {
    await supabase
      .from("notifications")
      .update({ is_read: true } as any)
      .eq("id", id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    await supabase
      .from("notifications")
      .update({ is_read: true } as any)
      .eq("user_id", user.id)
      .eq("is_read", false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  }, [user]);

  return { notifications, unreadCount, loading, markAsRead, markAllAsRead, refetch: fetchNotifications };
}
